import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE, getPhone, BackBar, Card, PrimaryBtn } from './healthShared';

const checkItems = (profile) => {
  const items = [
    { key: 'bp', label: 'Blood pressure' },
    { key: 'sugar', label: 'Blood sugar (fasting / HbA1c)' },
    { key: 'lipids', label: 'Lipid profile' },
    { key: 'weight', label: 'Weight & waist review' },
    { key: 'vaccines', label: 'Vaccinations (flu, Hep B, Tdap)' },
  ];
  const age = profile?.age || 0;
  const female = profile?.sex === 'Female';
  let cancer = 'Age-appropriate cancer screening';
  if (female && age >= 40) cancer = 'Cervical + breast screening (discuss mammography)';
  else if (female && age >= 25) cancer = 'Cervical screening (Pap / HPV)';
  else if (age >= 45) cancer = 'Colon cancer screening discussion';
  items.push({ key: 'cancer', label: cancer });
  items.push({ key: 'dental_eye', label: 'Dental & eye check' });
  return items;
};

export default function MyHealth({ profile, snapshot, go, onBack, onChecklistSaved }) {
  const [checks, setChecks] = useState(profile?.checklist || {});
  const [saving, setSaving] = useState(false);
  const items = checkItems(profile);
  const doneCount = items.filter((i) => checks[i.key]).length;

  const toggle = (k) => setChecks((c) => ({ ...c, [k]: !c[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/api/health-hub/checklist`, { phone: getPhone(), items: checks });
      toast.success('Health check saved');
      onChecklistSaved(checks);
    } catch {
      toast.error('Could not save checklist');
    }
    setSaving(false);
  };

  const metrics = [
    { l: 'BMI', v: snapshot?.bmi, s: snapshot?.bmi_category },
    { l: 'Weight', v: `${profile?.weight_kg} kg`, s: '' },
    { l: 'Waist', v: profile?.waist_cm ? `${profile.waist_cm} cm` : '—', s: '' },
    { l: 'Activity', v: profile?.activity, s: '' },
    { l: 'Sleep', v: `${profile?.sleep_hours} hrs`, s: `target ${snapshot?.sleep_target}` },
    { l: 'Hydration', v: `${snapshot?.hydration_l} L/day`, s: 'target' },
  ];

  return (
    <div data-testid="my-health-section">
      <BackBar title="My Health" onBack={onBack} />

      <Card className="p-5 mb-4" testId="my-health-metrics">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-black tracking-widest uppercase text-[#7FA183]">Health Snapshot</p>
          <button onClick={() => go('assessment')} data-testid="edit-profile-btn" className="text-xs font-bold text-[#2E4A3B] underline underline-offset-2">Edit profile</button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {metrics.map((m) => (
            <div key={m.l}>
              <p className="text-[10px] font-black tracking-widest uppercase text-[#9AA594]">{m.l}</p>
              <p className="text-base font-extrabold text-[#2E4A3B] mt-0.5 leading-tight">{m.v}</p>
              {m.s && <p className="text-[10px] text-[#9AA594]">{m.s}</p>}
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-[#F0F2E8] flex flex-wrap gap-2 text-xs text-[#66735F]">
          <span className="px-2.5 py-1 rounded-full bg-[#EDF2EB] font-semibold">{profile?.diet}</span>
          <span className="px-2.5 py-1 rounded-full bg-[#EDF2EB] font-semibold">Goal: {profile?.goal}</span>
          {(profile?.conditions || []).map((c) => <span key={c} className="px-2.5 py-1 rounded-full bg-[#FBF3EE] text-[#A85A3E] font-semibold">{c}</span>)}
        </div>
      </Card>

      <Card className="p-5" testId="health-check-card">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-black tracking-widest uppercase text-[#D97B5A]">My Health Check</p>
          <p className="text-xs font-bold text-[#7FA183]">{doneCount}/{items.length} reviewed</p>
        </div>
        <p className="text-[11px] text-[#9AA594] mb-4">Tick what you've reviewed or discussed in the last 12 months.</p>
        <div className="space-y-2">
          {items.map((i) => (
            <button
              key={i.key}
              onClick={() => toggle(i.key)}
              data-testid={`check-${i.key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${
                checks[i.key] ? 'bg-[#EDF2EB] border-[#9DB8A0]' : 'bg-white border-[#E3E5D8] hover:border-[#C7D3C3]'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 ${checks[i.key] ? 'bg-[#2E4A3B] text-white' : 'bg-[#F0F2E8] text-transparent'}`}>✓</span>
              <span className={`text-sm font-semibold ${checks[i.key] ? 'text-[#2E4A3B]' : 'text-[#44513F]'}`}>{i.label}</span>
            </button>
          ))}
        </div>
        <PrimaryBtn onClick={save} disabled={saving} testId="save-checklist-btn" className="w-full mt-4">
          {saving ? 'Saving…' : 'Save my health check'}
        </PrimaryBtn>
        <p className="text-[11px] text-[#9AA594] mt-3 text-center">For individualised medical decisions, discuss with your doctor.</p>
      </Card>
    </div>
  );
}
